import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShares } from '../hooks/useShares';
import { messaggioErrore } from '../utils/messaggioErrore';

// Create-link dialog, opened from a file/folder's context menu.
// target: { type: 'file'|'folder', id, name, raw }
export default function ShareModal({ target, allFolders, onClose }) {
  const { t } = useTranslation('share');
  const EXPIRY_OPTIONS = [
    { value: '',  label: t('sharemodal_expiry_never') },
    { value: '1', label: t('sharemodal_expiry_1day') },
    { value: '7', label: t('sharemodal_expiry_7days') },
    { value: '30', label: t('sharemodal_expiry_30days') },
  ];
  const { creating, createProgress, createShare } = useShares();
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword]       = useState('');
  const [expiryDays, setExpiryDays]   = useState('');
  const [maxDownloads, setMaxDownloads] = useState('');
  const [error, setError]             = useState('');
  const [result, setResult]           = useState(null); // { link }
  const [copied, setCopied]           = useState(false);

  async function handleCreate() {
    setError('');
    if (usePassword && !password.trim()) { setError(t('sharemodal_password_required_error')); return; }
    try {
      const expiresAt = expiryDays ? new Date(Date.now() + parseInt(expiryDays) * 86400000).toISOString() : null;
      const { link } = await createShare(target, {
        password: usePassword ? password.trim() : null,
        expiresAt,
        maxDownloads: maxDownloads ? parseInt(maxDownloads) : null,
        allFolders
      });
      setResult({ link });
    } catch (e) { setError(messaggioErrore(e, t)); }
  }

  function copyLink() {
    navigator.clipboard.writeText(result.link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }

  if (!target) return null;

  return (
    <div className="vz-modal-overlay" onClick={onClose}>
      <div className="vz-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <h3 className="vz-modal-title">{target.type === 'folder' ? t('sharemodal_title_folder') : t('sharemodal_title_file')}</h3>
        <p style={{ fontSize: 13, color: 'var(--text3)', margin: '-6px 0 0' }}>
          <strong style={{ color: 'var(--text)' }}>{target.name}</strong>
        </p>

        {!result && (
          <>
            <label className="share-row">
              <input type="checkbox" checked={usePassword} onChange={e => setUsePassword(e.target.checked)} />
              <span>{t('sharemodal_password_protect_label')}</span>
            </label>
            {usePassword && (
              <input className="vz-modal-input" type="text" placeholder={t('sharemodal_password_placeholder')}
                value={password} onChange={e => setPassword(e.target.value)} autoFocus />
            )}

            <div className="share-row-split">
              <div>
                <label className="share-label">{t('sharemodal_expiry_label')}</label>
                <select className="vz-modal-input" value={expiryDays} onChange={e => setExpiryDays(e.target.value)}>
                  {EXPIRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="share-label">{t('sharemodal_max_downloads_label')}</label>
                <input className="vz-modal-input" type="number" min="1" placeholder={t('sharemodal_max_downloads_placeholder')}
                  value={maxDownloads} onChange={e => setMaxDownloads(e.target.value)} />
              </div>
            </div>

            {error && <p className="vz-modal-err">{error}</p>}

            {creating && (
              <p style={{ fontSize: 12.5, color: 'var(--text3)' }}>
                {createProgress.total > 1 ? t('sharemodal_encrypting_progress', { done: createProgress.done, total: createProgress.total }) : t('sharemodal_encrypting_single')}
              </p>
            )}

            <div className="vz-modal-btns">
              <button className="vz-btn vz-btn-ghost" onClick={onClose} disabled={creating}>{t('sharemodal_cancel_btn')}</button>
              <button className="vz-btn vz-btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? t('sharemodal_creating_btn') : t('sharemodal_create_link_btn')}
              </button>
            </div>
          </>
        )}

        {result && (
          <>
            <div className="rt-box" style={{ margin: '4px 0' }}>
              <div className="rt-token" style={{ color: 'var(--text)', fontWeight: 500 }}>{result.link}</div>
            </div>
            {usePassword && (
              <p className="rt-alert">{t('sharemodal_password_separate_warning')}</p>
            )}
            <div className="vz-modal-btns">
              <button className="vz-btn vz-btn-ghost" onClick={onClose}>{t('sharemodal_close_btn')}</button>
              <button className="vz-btn vz-btn-primary" onClick={copyLink}>{copied ? t('sharemodal_copied_btn') : t('sharemodal_copy_link_btn')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
